if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.mwGalleryIndex = 0;
        this.mwLightboxIndex = 0;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          requestAnimationFrame(() => this.initializeMamaWynjaGallery());
        });

        this.addEventListener(
          'click',
          (event) => {
            const galleryButton = event.target.closest('.mw-quickview-gallery-controls .slider-button');
            if (galleryButton) {
              event.preventDefault();
              event.stopImmediatePropagation();

              const delta = galleryButton.classList.contains('slider-button--next') ? 1 : -1;
              this.setMamaWynjaGalleryIndex(this.mwGalleryIndex + delta);
              return;
            }

            const image = event.target.closest('.mw-quickview-enhanced .product__media img');
            if (image) {
              event.preventDefault();
              event.stopImmediatePropagation();

              const slide = image.closest('.product__media-item');
              const slides = this.getMamaWynjaGallerySlides();
              const index = Math.max(0, slides.indexOf(slide));
              this.openMamaWynjaLightbox(index);
              return;
            }

            const lightboxPrev = event.target.closest('.mw-quickview-lightbox__prev');
            const lightboxNext = event.target.closest('.mw-quickview-lightbox__next');
            const lightboxClose = event.target.closest('.mw-quickview-lightbox__close');

            if (lightboxPrev || lightboxNext || lightboxClose) {
              event.preventDefault();
              event.stopImmediatePropagation();

              if (lightboxClose) {
                this.closeMamaWynjaLightbox();
              } else {
                this.setMamaWynjaLightboxIndex(
                  this.mwLightboxIndex + (lightboxNext ? 1 : -1)
                );
              }
              return;
            }

            const lightbox = event.target.closest('.mw-quickview-lightbox');
            if (lightbox && event.target === lightbox) {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.closeMamaWynjaLightbox();
            }
          },
          true
        );

        this.addEventListener('keydown', (event) => {
          const lightbox = this.querySelector('.mw-quickview-lightbox:not([hidden])');
          if (!lightbox) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeMamaWynjaLightbox();
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.setMamaWynjaLightboxIndex(this.mwLightboxIndex - 1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.setMamaWynjaLightboxIndex(this.mwLightboxIndex + 1);
          }
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            this.classList.add('mw-quickview-modal');
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            requestAnimationFrame(() => this.initializeMamaWynjaGallery());

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        this.enhanceMamaWynjaQuickView(productElement);

        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      enhanceMamaWynjaQuickView(productElement) {
        if (!productElement) return;

        productElement.classList.add('mw-quickview-enhanced');

        const galleryItems = productElement.querySelectorAll('.product__media-item');
        const sliderButtons = productElement.querySelector('[id^="GalleryViewer-"] > .slider-buttons');

        if (galleryItems.length > 1 && sliderButtons) {
          sliderButtons.classList.remove('quick-add-hidden');
          sliderButtons.classList.add('mw-quickview-gallery-controls');
        }

        const description = productElement.querySelector('.product__description');
        const viewDetails = productElement.querySelector('.product__view-details');
        const buyForm = productElement.querySelector('product-form');

        if (description) {
          description.classList.remove('quick-add-hidden');
          description.classList.add('mw-quickview-description');

          const text = description.textContent.replace(/\s+/g, ' ').trim();
          description.innerHTML = '';

          const excerpt = document.createElement('p');
          excerpt.className = 'mw-quickview-description__excerpt';
          excerpt.textContent = text;
          description.appendChild(excerpt);

          if (buyForm) buyForm.insertAdjacentElement('afterend', description);
        }

        if (viewDetails) {
          viewDetails.classList.add('mw-quickview-read-more');
          viewDetails.innerHTML = 'Läs mer <span aria-hidden="true">→</span>';

          if (description) {
            description.insertAdjacentElement('afterend', viewDetails);
          }
        }
      }

      getMamaWynjaGallerySlides() {
        return Array.from(
          this.modalContent.querySelectorAll('.mw-quickview-enhanced .product__media-item')
        );
      }

      initializeMamaWynjaGallery() {
        const slides = this.getMamaWynjaGallerySlides();
        if (!slides.length) return;

        this.mwGalleryIndex = Math.max(0, Math.min(this.mwGalleryIndex || 0, slides.length - 1));
        this.setMamaWynjaGalleryIndex(this.mwGalleryIndex, false);

        slides.forEach((slide) => {
          const image = slide.querySelector('.product__media img');
          if (image) {
            image.setAttribute('role', 'button');
            image.setAttribute('tabindex', '0');
            image.setAttribute('aria-label', 'Visa större bild');
          }
        });
      }

      setMamaWynjaGalleryIndex(index, updateCounter = true) {
        const slides = this.getMamaWynjaGallerySlides();
        const controls = this.modalContent.querySelector('.mw-quickview-gallery-controls');
        if (!slides.length) return;

        const nextIndex = Math.max(0, Math.min(slides.length - 1, index));
        this.mwGalleryIndex = nextIndex;

        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-mw-active', slideIndex === nextIndex);
          slide.toggleAttribute('hidden', slideIndex !== nextIndex);
        });

        if (!controls) return;

        const current = controls.querySelector('.slider-counter--current');
        const total = controls.querySelector('.slider-counter--total');
        const prev = controls.querySelector('.slider-button--prev');
        const next = controls.querySelector('.slider-button--next');

        if (updateCounter && current) current.textContent = String(nextIndex + 1);
        if (total) total.textContent = String(slides.length);
        if (prev) prev.disabled = nextIndex <= 0;
        if (next) next.disabled = nextIndex >= slides.length - 1;
      }

      getMamaWynjaLightboxImages() {
        return this.getMamaWynjaGallerySlides()
          .map((slide) => slide.querySelector('.product__media img'))
          .filter(Boolean)
          .map((img) => {
            let src = img.currentSrc || img.src;

            if (img.srcset) {
              const candidates = img.srcset
                .split(',')
                .map((entry) => entry.trim())
                .map((entry) => {
                  const parts = entry.split(/\s+/);
                  const width = parseInt(parts[1], 10) || 0;
                  return { src: parts[0], width };
                })
                .filter((candidate) => candidate.src);

              candidates.sort((a, b) => b.width - a.width);
              if (candidates[0]?.src) src = candidates[0].src;
            }

            return {
              src,
              alt: img.alt || '',
            };
          });
      }

      ensureMamaWynjaLightbox() {
        let lightbox = this.querySelector('.mw-quickview-lightbox');
        if (lightbox) return lightbox;

        lightbox = document.createElement('div');
        lightbox.className = 'mw-quickview-lightbox';
        lightbox.hidden = true;
        lightbox.innerHTML = `
          <button type="button" class="mw-quickview-lightbox__close" aria-label="Stäng bildvisning">×</button>
          <button type="button" class="mw-quickview-lightbox__nav mw-quickview-lightbox__prev" aria-label="Föregående bild">‹</button>
          <figure class="mw-quickview-lightbox__figure">
            <img class="mw-quickview-lightbox__image" alt="">
            <figcaption class="mw-quickview-lightbox__counter"></figcaption>
          </figure>
          <button type="button" class="mw-quickview-lightbox__nav mw-quickview-lightbox__next" aria-label="Nästa bild">›</button>
        `;
        this.appendChild(lightbox);
        return lightbox;
      }

      openMamaWynjaLightbox(index) {
        const images = this.getMamaWynjaLightboxImages();
        if (!images.length) return;

        const lightbox = this.ensureMamaWynjaLightbox();
        lightbox.hidden = false;
        document.documentElement.classList.add('overflow-hidden');
        this.setMamaWynjaLightboxIndex(index);
        lightbox.querySelector('.mw-quickview-lightbox__close')?.focus();
      }

      closeMamaWynjaLightbox() {
        const lightbox = this.querySelector('.mw-quickview-lightbox');
        if (!lightbox) return;

        lightbox.hidden = true;
        document.documentElement.classList.remove('overflow-hidden');
      }

      setMamaWynjaLightboxIndex(index) {
        const images = this.getMamaWynjaLightboxImages();
        const lightbox = this.ensureMamaWynjaLightbox();
        if (!images.length || !lightbox) return;

        const nextIndex = Math.max(0, Math.min(images.length - 1, index));
        this.mwLightboxIndex = nextIndex;

        const image = lightbox.querySelector('.mw-quickview-lightbox__image');
        const counter = lightbox.querySelector('.mw-quickview-lightbox__counter');
        const prev = lightbox.querySelector('.mw-quickview-lightbox__prev');
        const next = lightbox.querySelector('.mw-quickview-lightbox__next');

        if (image) {
          image.src = images[nextIndex].src;
          image.alt = images[nextIndex].alt;
        }
        if (counter) counter.textContent = `${nextIndex + 1} / ${images.length}`;
        if (prev) prev.disabled = nextIndex <= 0;
        if (next) next.disabled = nextIndex >= images.length - 1;
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
